<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Поступления</h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Приходные накладные от контрагентов</p>
          </div>

          <Button
            icon="pi pi-plus"
            label="Новое поступление"
            severity="success"
            class="!px-4 !py-2 !text-sm !shadow-sm"
            @click="goToArrivalForm"
          />
        </div>

        <div class="min-h-0 flex flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
          <div class="mb-4 flex shrink-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="relative w-full sm:w-80">
              <i class="pi pi-search pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400"></i>
              <input
                v-model.trim="searchQuery"
                type="text"
                placeholder="Поиск по контрагенту или номеру..."
                class="h-11 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-400"
              />
            </div>

            <Button
              icon="pi pi-refresh"
              label="Обновить"
              severity="secondary"
              outlined
              class="!px-4 !py-2 !text-sm"
              @click="getArrivals"
            />
          </div>

          <div class="min-h-0 flex flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div class="min-h-0 h-full flex-1 overflow-x-auto overflow-y-auto">
              <table class="min-w-full border-separate border-spacing-0 text-sm">
                <thead class="bg-slate-50">
                  <tr>
                    <th class="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Контрагент</th>
                    <th class="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Дата</th>
                    <th class="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-4 py-3 text-center text-xs font-medium uppercase tracking-wide text-slate-500">Позиций</th>
                    <th class="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Сумма</th>
                    <th class="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-4 py-3 text-center text-xs font-medium uppercase tracking-wide text-slate-500">Статус</th>
                  </tr>
                </thead>

                <tbody class="bg-white">
                  <tr v-if="!filteredArrivals.length">
                    <td colspan="5" class="px-4 py-12 text-center text-slate-500">Поступления не найдены</td>
                  </tr>

                  <tr
                    v-for="item in filteredArrivals"
                    :key="item.arrival_id"
                    class="cursor-pointer transition hover:bg-emerald-50/40"
                    @click="goToArrivalList(item.arrival_id)"
                  >
                    <td class="border-b border-slate-200 px-4 py-4">
                      <div class="flex items-center gap-3">
                        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                          <i class="pi pi-truck text-sm"></i>
                        </div>
                        <div class="min-w-0">
                          <p class="truncate font-semibold text-slate-900">{{ item.counterparty || '—' }}</p>
                          <p class="mt-0.5 text-xs text-slate-500">Накладная №{{ item.arrival_id }}</p>
                        </div>
                      </div>
                    </td>
                    <td class="border-b border-slate-200 px-4 py-4 text-slate-600">
                      {{ formatDate(item.date) }}
                    </td>
                    <td class="border-b border-slate-200 px-4 py-4 text-center text-slate-800">
                      {{ Number(item.positions_count || 0) }}
                    </td>
                    <td class="border-b border-slate-200 px-4 py-4 text-right font-semibold text-slate-900">
                      {{ formatCurrency(item.total) }}
                    </td>
                    <td class="border-b border-slate-200 px-4 py-4 text-center">
                      <span class="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        Принято
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="sticky bottom-0 z-10 shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-3 shadow-[0_-10px_24px_rgba(15,23,42,0.06)]">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="text-sm text-slate-500">
                  Накладных в списке:
                  <span class="font-semibold text-slate-900">{{ filteredArrivals.length }}</span>
                </div>
                <div class="flex items-center justify-between gap-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 sm:min-w-[320px]">
                  <span class="text-sm font-medium text-slate-700">Общая сумма закупки</span>
                  <span class="text-lg font-semibold text-emerald-700">{{ formatCurrency(filteredPurchaseTotal) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from '../lib/axios'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'

const router = useRouter()
const arrivals = ref([])
const searchQuery = ref('')

const filteredArrivals = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return arrivals.value

  return arrivals.value.filter((item) => {
    const haystack = [
      item.counterparty,
      item.arrival_id
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(q)
  })
})

const filteredPurchaseTotal = computed(() => {
  return filteredArrivals.value.reduce((sum, item) => sum + Number(item.total || 0), 0)
})

const formatDate = (value) => {
  if (!value) return '—'
  return dayjs(value).format('DD.MM.YYYY')
}

const formatCurrency = (value) => {
  return `${Number(value || 0).toLocaleString('ru-RU')} ₸`
}

const getArrivals = async () => {
  const res = await axios.get('/api/arrival-list')
  arrivals.value = Array.isArray(res.data) ? res.data : Object.values(res.data || {})
}

const goToArrivalList = (arrivalId) => {
  router.push({ name: 'ArrivalList', params: { id: arrivalId } })
}

const goToArrivalForm = () => {
  router.push({ name: 'ArrivalForm' })
}

onMounted(() => {
  getArrivals()
})
</script>
